import type {
  ICity,
  ISelectList,
} from "../components/Address/AddressForm/address-form.interface";
import { EAddressType, EStoreIds } from "../constants";
import type {
  IAddress,
  IGetFormFieldRequirements,
  IUserAddress,
} from "../types";

export const rearrangeAddresses = (
  addresses: IUserAddress[],
  selectedAddressId: string,
): IUserAddress[] => {
  const selectedAddress = addresses.find(
    (address) => address.id === selectedAddressId,
  );

  if (!selectedAddress) return addresses;
  const rearrangedAddresses = [
    selectedAddress,
    ...addresses.filter((address) => address.id !== selectedAddressId),
  ];

  return rearrangedAddresses;
};

export const countryFlagIconMap: Record<string, string> = {
  MAFUAE: "AE_3cd82801fe.png",
  MAFQAT: "QA_b4310ae52c.png",
  MAFKWT: "KW_02ec133b23.png",
  MAFEGY: "EG_bceab67933.png",
  MAFGEO: "GE_5ee57ae866.png",
  MAFJOR: "JO_17e24fa030.png",
  MAFLBN: "LB_ceafd0e11a.png",
  MAFPAK: "PK_3ea43c29b5.png",
  MAFSAU: "SA_090b37bfc2.png",
  MAFKEN: "KE_87c969bc38.png",
};

export const getCityList = (cities: ICity[] = []): ISelectList[] => {
  return cities.map((city) => ({
    label: city.cityNameEN,
    value: city.cityNameEN,
  }));
};

export const getZoneList = (
  cities: ICity[] = [],
): Record<string, ISelectList[]> | null => {
  if (!cities.length) return null;
  return cities.reduce(
    (zoneMap: Record<string, { label: string; value: string }[]>, city) => {
      const cityKey = city.cityNameEN;
      zoneMap[cityKey] = city.zones.map((zone) => ({
        label: zone.zoneNo,
        value: zone.zoneNo,
      }));
      return zoneMap;
    },
    {},
  );
};

export const getAreaList = (
  cities: ICity[] = [],
): Record<string, ISelectList[]> | null => {
  if (!cities.length) return null;
  return cities.reduce(
    (areaMap: Record<string, { label: string; value: string }[]>, city) => {
      city.zones.forEach((zone) => {
        const zoneKey = zone.zoneNo;
        areaMap[zoneKey] = zone.areas.map((area) => ({
          label: area.areaNameEN,
          value: area.areaNameEN,
        }));
      });
      return areaMap;
    },
    {},
  );
};

export const getAddressLabel = (address: IAddress): string => {
  const label = address.addressLabel?.toLowerCase();
  return label === EAddressType.HOME || label === EAddressType.OFFICE
    ? label
    : EAddressType.OTHER;
};

export const getFormFieldRequirements = (
  storeId: EStoreIds,
): IGetFormFieldRequirements => {
  return {
    isAreaFormFieldRequired: storeId !== EStoreIds.MAFQAT,
    isDefaultFormFieldRequired:
      storeId !== EStoreIds.MAFKWT && storeId !== EStoreIds.MAFQAT,
    isEmirateFormFieldRequired: storeId === EStoreIds.MAFUAE,
    isKuwaitFormFieldRequired: storeId === EStoreIds.MAFKWT,
    isQatarFormFieldRequired: storeId === EStoreIds.MAFQAT,
  };
};

export const filterAddressParts = (
  parts?: string | string[],
): string | string[] => {
  if (typeof parts === "string") {
    return parts && parts !== "undefined" ? parts : "";
  }

  return (
    parts?.filter((part): part is string =>
      Boolean(part && part !== "undefined"),
    ) || []
  );
};
